/* eslint-disable camelcase */
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { numberFormat } from '@/helpers/numbers'
import { Box, CardContent, GridLegacy as Grid, Stack, Typography, useTheme } from '@mui/material'
import { PieChart } from '@mui/x-charts'

import React from 'react'
import { useTranslation } from 'react-i18next'

const canteenMenuItemNutritionalItemDataFragment = graphql(`
  fragment CanteenMenuItemNutritional_ItemDataFragment on MenuItem {
    _id
    macros_kcal
    macros_protein
    macros_carbs
    macros_sugar
    macros_fat
    macros_saturates
    macros_fibre
    macros_salt
  }
`)

type Props = {
  companyName: string
  itemData: FragmentType<typeof canteenMenuItemNutritionalItemDataFragment>
}

const MenuItemNutritional: React.FC<Props> = ({ companyName, itemData: itemDataProp }) => {
  const {
    macros_kcal,
    macros_protein,
    macros_carbs,
    macros_sugar,
    macros_fat,
    macros_saturates,
    macros_fibre,
    macros_salt,
  } = useFragment(canteenMenuItemNutritionalItemDataFragment, itemDataProp)

  const theme = useTheme()
  const { t } = useTranslation()

  const noData =
    !macros_kcal &&
    !macros_protein &&
    !macros_carbs &&
    !macros_sugar &&
    !macros_fat &&
    !macros_saturates &&
    !macros_fibre &&
    !macros_salt

  return (
    <CardContent>
      <Stack spacing={3} direction="column">
        <Stack direction="row" spacing={1}>
          <Typography variant="subtitle3">
            {!noData ? 'Nutritional information' : 'There is no nutritional information yet'}
          </Typography>
          {!noData ? (
            <Typography color="text.secondary" variant="body2">
              ({t('cc:allInformationProvidedBy')} {companyName})
            </Typography>
          ) : null}
        </Stack>
        {noData ? (
          <Typography variant="body1" color="text.secondary">
            We are waiting for information to be supplied by {companyName}
          </Typography>
        ) : null}
        {!noData ? (
          <>
            <PieChart
              series={[
                {
                  data: [
                    {
                      id: 0,
                      value: macros_protein || 0,
                      label: `Protein`,
                      color: theme.palette.primary.main,
                    },
                    {
                      id: 1,
                      value: macros_carbs || 0,
                      label: `Carbs`,
                      color: theme.palette.info.main,
                    },
                    {
                      id: 2,
                      value: macros_fat || 0,
                      label: `Fat`,
                      color: theme.palette.warning.main,
                    },
                  ],
                },
              ]}
              width={500}
              height={200}
            />
            <Stack direction="row" spacing={0.5}>
              <Typography variant="subtitle3">Base dish</Typography>
              <Typography color="text.secondary">(serves 1)</Typography>
            </Stack>
            <Grid container spacing={1}>
              {[
                {
                  value: macros_kcal,
                  unit: 'kcal',
                  label: 'energy',
                  decimalPlaces: 0,
                },
                {
                  value: macros_protein,
                  unit: 'g',
                  label: 'protein',
                  decimalPlaces: 0,
                },
                {
                  value: macros_carbs,
                  unit: 'g',
                  label: 'carbs',
                  decimalPlaces: 0,
                },
                {
                  value: macros_sugar,
                  unit: 'g',
                  label: 'sugars',
                  decimalPlaces: 0,
                },
                {
                  value: macros_fat,
                  unit: 'g',
                  label: 'fat',
                  decimalPlaces: 0,
                },
                {
                  value: macros_saturates,
                  unit: 'g',
                  label: 'sat. fat',
                  decimalPlaces: 0,
                },
                {
                  value: macros_fibre,
                  unit: 'g',
                  label: 'fibre',
                  decimalPlaces: 0,
                },
                {
                  value: macros_salt,
                  unit: 'mg',
                  label: 'salt',
                  decimalPlaces: 0,
                },
              ].map((item) => (
                <Grid key={`${item.value}-${item.label}`} item xs={4} sm={3} md={2}>
                  <Box
                    sx={{
                      borderRadius: '6px',
                      paddingY: 2,
                      paddingX: 1,
                      backgroundColor: 'background.default',
                    }}>
                    <Typography textAlign="center">
                      {numberFormat(item.value || 0, item.decimalPlaces)}
                      {item.unit}
                    </Typography>
                    <Typography textAlign="center">{item.label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </>
        ) : null}
      </Stack>
    </CardContent>
  )
}

export default MenuItemNutritional
